import { z } from "zod";
import { BillLineItemKind, BillSource, BillStatus } from "@common/types/bill";
import {
    documentLineScheduleSchema,
    documentScheduleBaseSchema,
} from "../accounting/scheduleDocumentDisplay.schema";
import { counterpartySchema } from "../../counterparty.schema";
import { classAssignmentSchema } from "../../class.schema";
import { financialAccountWithIntegrationAccountsResponseSchema } from "../../financialAccount.schema";
import { accountOverrideEntryResponseSchema } from "../accounting/account-override.schema";
import {
    financialDocumentLinksResponseSchema,
    toFinancialDocumentIds,
} from "../../financialDocument/financial-document-links-response.schema";

export const billLineItemScheduleSchema = documentLineScheduleSchema;

export type BillLineItemSchedule = z.infer<typeof billLineItemScheduleSchema>;

// Scope tells the bill-level Schedule column which of its states to render:
// a whole-bill application, or one or more line-scoped ones.
export const billScheduleScopeSchema = z.enum(["bill", "lines"]);

export const billScheduleSchema = documentScheduleBaseSchema.extend({
    scope: billScheduleScopeSchema,
});

export type BillSchedule = z.infer<typeof billScheduleSchema>;

export const billLineItemResponseSchema = z.object({
    id: z.string(),
    amount: z.number(),
    matchedAmount: z.number(),
    schedule: billLineItemScheduleSchema.optional(),
    unitAmount: z.number().nullable().optional(),
    currency: z.string(),
    description: z.string().nullable(),
    quantityDecimal: z.number().nullable(),
    sourceId: z.string(),
    accountId: z.string().nullable().optional(),
    accountsPayableId: z.string().nullable().optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    billId: z.string(),
    kind: z.nativeEnum(BillLineItemKind),
    accountOverrides: z.array(accountOverrideEntryResponseSchema).optional(),
    accountsPayableOverrides: z
        .array(accountOverrideEntryResponseSchema)
        .optional(),
});

/**
 * Owns the BillEntity → Bill DTO boundary: unknown keys (e.g.
 * `sourceData`, line back-references) are stripped by Zod, and
 * `financialDocumentLinks` are reshaped into `financialDocumentIds`.
 * Controllers return entity rows directly; `validateResponses` produces
 * the DTO.
 */
export const billResponseSchema = z
    .object({
        schedule: billScheduleSchema.optional(),
        id: z.string(),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
        due: z.coerce.date().nullable(),
        paidAt: z.coerce.date().nullable(),
        dateOnly: z.string().nullable(),
        paidDateOnly: z.string().nullable(),
        dueDateOnly: z.string().nullable(),
        total: z.number(),
        subtotal: z.number(),
        tax: z.number(),
        discount: z.number(),
        deposit: z.number().nullable(),
        currency: z.string(),
        counterparty: counterpartySchema.nullable().optional(),
        description: z.string().nullable(),
        memo: z.string().nullable(),
        status: z.nativeEnum(BillStatus),
        externalStatus: z.nativeEnum(BillStatus).nullable(),
        source: z.nativeEnum(BillSource),
        sourceId: z.string().nullable(),
        lines: z.array(billLineItemResponseSchema).optional(),
        entityId: z.number(),
        financialAccountId: z.number().nullable(),
        financialAccount: financialAccountWithIntegrationAccountsResponseSchema
            .nullable()
            .optional(),
        invoiceNumber: z.string().nullable(),
        financialDocumentLinks: financialDocumentLinksResponseSchema,
    })
    .transform(({ financialDocumentLinks, ...bill }) => ({
        ...bill,
        financialDocumentIds: toFinancialDocumentIds(financialDocumentLinks),
    }));
export const billsPageResponseSchema = z.object({
    total: z.number(),
    pageCount: z.number(),
    data: z.array(billResponseSchema),
});
